import { 
  Button, 
  Box, 
  Typography, 
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Avatar,
  Badge,
  TextField,
  InputAdornment
} from '@mui/material'
import { 
  Edit, 
  Delete, 
  Add, 
  CheckCircle, 
  Cancel,
  NotificationsActive,
  Search
} from '@mui/icons-material'
import { DataGrid, GridRenderCellParams, GridColDef } from '@mui/x-data-grid'
import { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { fetchSetupTemplates, fetchSetupTemplateDetails } from '../../../redux/notificationCenter'
import { AppDispatch } from '../../../redux/store'

import { NotificationSetupTemplate, NotificationMessageTemplate } from '../../../services/Hub/notificationCenter/types'
import {
  FieldDefinition,
  SelectOption
} from '../../../shared/components/AdvancedExpressionBuilder/types'
import { useDebounce } from '../../../hooks/useDebounce'
import UpsertSetupTemplateModal from './UpsertSetupTemplateModal'
import DeleteSetupTemplateModal from './DeleteSetupTemplateModal'

type Props = {
  setupTemplates: NotificationSetupTemplate[]
  messageTemplates?: NotificationMessageTemplate[]
  fetchFieldCatalog?: (search?: string) => Promise<FieldDefinition[]>
  fetchResourceValues?: (resourceId: string, search?: string) => Promise<SelectOption[]>
  isLoading?: boolean
}

export default function CanonicalNotificationsTab({
  setupTemplates,
  messageTemplates = [],
  fetchFieldCatalog,
  fetchResourceValues,
  isLoading = false
}: Props) {
  const [upsertModalOpen, setUpsertModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationSetupTemplate | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(fetchSetupTemplates({ includeInactive: true, name: debouncedSearchTerm }))
  }, [debouncedSearchTerm, dispatch])

  // Default fetch functions if not provided
  const defaultFetchFieldCatalog = async (): Promise<FieldDefinition[]> => {
    // TODO: Implement actual field catalog fetching
    return []
  }

  const defaultFetchResourceValues = async (): Promise<SelectOption[]> => {
    // TODO: Implement actual resource values fetching
    return []
  }

  const handleEdit = (template: NotificationSetupTemplate) => {
    dispatch(fetchSetupTemplateDetails(template.id));
    setSelectedTemplate(template)
    setUpsertModalOpen(true)
  }

  const handleDelete = (template: NotificationSetupTemplate) => {
    setSelectedTemplate(template)
    setDeleteModalOpen(true)
  }

  const handleCloseModals = () => {
    setSelectedTemplate(null)
    setUpsertModalOpen(false)
    setDeleteModalOpen(false)
  }

  const columns: GridColDef<NotificationSetupTemplate>[] = [
    {
      field: 'name',
      headerName: 'Nome',
      flex: 2,
      minWidth: 300,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ py: 1 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {params.value}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Configuração #{params.row.id}
          </Typography>
        </Box>
      )
    },
    {
      field: 'source',
      headerName: 'Fonte',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Chip 
          label={params.value} 
          variant="outlined"
          size="small"
          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
        />
      )
    },
    {
      field: 'createdAt',
      headerName: 'Criado em',
      width: 150,
      renderCell: (params: GridRenderCellParams) => {
        const date = params.value ? new Date(params.value) : null
        return (
          <Typography variant="body2" color="text.secondary">
            {date ? date.toLocaleDateString('pt-BR') : 'N/A'}
          </Typography>
        )
      }
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          icon={params.value ? <CheckCircle /> : <Cancel />}
          label={params.value ? 'Ativo' : 'Inativo'}
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
          size="small"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 120,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Editar">
            <IconButton 
              size="small" 
              onClick={() => handleEdit(params.row)}
              sx={{ 
                bgcolor: 'primary.main', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' }
              }}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir">
            <IconButton 
              size="small" 
              onClick={() => handleDelete(params.row)}
              sx={{ 
                bgcolor: 'error.main', 
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' }
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ]

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={400}>
        <Typography>Loading notifications...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="flex-start" 
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h6" gutterBottom fontWeight={600}>
            Notificações Canônicas
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure templates de notificação e suas condições de disparo.
          </Typography>
        </Box>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => {
            setSelectedTemplate(null)
            setUpsertModalOpen(true)
          }}
          size="large"
          sx={{ 
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Nova Notificação
        </Button>
      </Stack>

      {/* Search Section */}
      <Card variant="outlined" sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent sx={{ py: 2 }}>
          <TextField
            fullWidth
            placeholder="Buscar por nome ou fonte..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Badge 
                badgeContent={setupTemplates.filter(t => t.isActive).length} 
                color="success"
                max={999}
              >
                <Avatar sx={{ bgcolor: 'success.light' }}>
                  <CheckCircle />
                </Avatar>
              </Badge>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Notificações Ativas
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {setupTemplates.filter(t => t.isActive).length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
        
        <Card variant="outlined" sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{ bgcolor: 'primary.light' }}>
                <NotificationsActive />
              </Avatar>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total de Templates
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {setupTemplates.length}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      {/* Data Grid */}
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <DataGrid
          rows={setupTemplates}
          columns={columns}
          loading={isLoading}
          getRowId={row => row.id}
          disableRowSelectionOnClick
          rowHeight={60}
          sx={{ 
            border: 'none',
            '& .MuiDataGrid-columnHeaders': {
              bgcolor: 'grey.50',
              borderBottom: '2px solid',
              borderColor: 'divider'
            },
            '& .MuiDataGrid-cell': {
              borderColor: 'grey.100',
              py: 1
            },
            '& .MuiDataGrid-row': {
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }
          }}
          initialState={{
            pagination: {
              paginationModel: { pageSize: 10 }
            }
          }}
          pageSizeOptions={[5, 10, 25, 50]}
        />
      </Card>

      <UpsertSetupTemplateModal
        open={upsertModalOpen}
        onClose={handleCloseModals}
        fetchFieldCatalog={fetchFieldCatalog || defaultFetchFieldCatalog}
        fetchResourceValues={fetchResourceValues || defaultFetchResourceValues}
        messageTemplates={messageTemplates}
        setupTemplates={setupTemplates}
        template={selectedTemplate}
      />
      
      {selectedTemplate && (
        <DeleteSetupTemplateModal
          open={deleteModalOpen}
          onClose={handleCloseModals}
          templateId={selectedTemplate.id}
          templateName={selectedTemplate.name}
        />
      )}
    </Box>
  )
}
